/**
 * Shared hook for monitoring app state changes (background/foreground)
 *
 * Provides callbacks for when app goes to background or returns to foreground.
 * Subscribes to AppState directly; the WebSocket hook that first used this
 * pattern was retired in SE-8432.
 * @remarks
 * This hook is used to detect when the user switches away from the app
 * (to background/inactive) or returns to the app (to active). It provides
 * callback functions that execute during these transitions.
 */

import { useEffect, useState } from "react";
import { AppState, AppStateStatus, Platform } from "react-native";

/**
 * Callback options for app state changes
 */
interface UseAppStateListenerOptions {
  /** Callback when app goes to background or inactive */
  onBackground?: () => void;
  /** Callback when app returns to foreground (active) */
  onForeground?: () => void;
  /** Callback when app becomes active */
  onActive?: () => void;
  /** Callback when app becomes inactive */
  onInactive?: () => void;
}

/**
 * Hook return interface
 */
interface UseAppStateListenerReturn {
  /** Current app state */
  appState: AppStateStatus;
  /** Whether app is currently active */
  isActive: boolean;
  /** Whether app is currently in background or inactive */
  isBackground: boolean;
}

/**
 * Hook to listen for app state changes and execute callbacks
 * @param options - Callback functions for different app state transitions
 * @returns Current app state and helper booleans
 * @example
 * ```typescript
 * const { isActive, isBackground } = useAppStateListener({
 *   onBackground: () => {
 *     console.log('App went to background');
 *     // Clean up resources
 *   },
 *   onForeground: () => {
 *     console.log('App returned to foreground');
 *     // Resume operations
 *   },
 * });
 * ```
 */
function useAppStateListener(
  options: UseAppStateListenerOptions = {}
): UseAppStateListenerReturn {
  const { onBackground, onForeground, onActive, onInactive } = options;
  const [appState, setAppState] = useState<AppStateStatus>(
    AppState.currentState
  );

  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      const previousState = appState;

      // App going to background or inactive
      if (
        previousState.match(/active/) &&
        nextAppState.match(/inactive|background/)
      ) {
        onBackground?.();
      }

      // App returning to foreground (active)
      if (
        previousState.match(/inactive|background/) &&
        nextAppState === "active"
      ) {
        onForeground?.();
      }

      // Specific state callbacks
      if (nextAppState === "active") {
        onActive?.();
      } else if (nextAppState === "inactive") {
        onInactive?.();
      }

      setAppState(nextAppState);
    };

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange
    );

    // Add web visibility change listener for tab switching
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab became hidden (user switched away)
        onBackground?.();
      } else {
        // Tab became visible (user switched back)
        onForeground?.();
      }
    };

    if (Platform.OS === "web" && typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      subscription.remove();
      if (Platform.OS === "web" && typeof document !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      }
    };
  }, [appState, onBackground, onForeground, onActive, onInactive]);

  return {
    appState,
    isActive: appState === "active",
    isBackground: appState.match(/inactive|background/) !== null,
  };
}

export { useAppStateListener };
export type { UseAppStateListenerOptions, UseAppStateListenerReturn };
